import * as Sentry from "@sentry/browser";
import {
  Drawer,
  Table,
  message,
  Tag,
} from "antd";
import { ControlOutlined } from "@ant-design/icons";
import { FormInstance } from "antd/lib/form";
import { ColumnType } from "antd/lib/table";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import moment from "moment";
import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router";
import { NavLink } from "react-router-dom";
import AdvancedSearchModal, {
  AdvancedSearchReturn,
  IField,
} from "../components/AdvancedSearchModal";
import { PageToolbar } from "../components/PageToolbar";
import Card from "../components/Card";
import ExportData from "../components/ExportData";
import InteractionSection from "../components/InteractionSection";
import DelayUpdateModal from "../components/DelayUpdateModal";
import {
  ASSIGN_ATTACHMENT_TO_CUSTOMER_INTERACTION,
  ASSIGN_NOTE_TO_CUSTOMER_INTERACTION,
  CLOSE_CUSTOMER_INTERACTION,
  CREATE_CUSTOMER_TICKET,
  CUSTOMER_INTERACTIONS_QUERY,
  GET_CUSTOMER_INTERACTION_CHANGELOG,
  GET_CUSTOMER_INTERACTION_NOTIFICATION_PREVIEW,
  GET_CUSTOMER_INTERACTION_QUERY,
  HOLD_CUSTOMER_INTERACTION,
  OPEN_CUSTOMER_INTERACTION,
  SEND_CUSTOMER_INTERACTION_DELAY_UPDATE,
  UPDATE_CUSTOMER_INTERACTION,
} from "../gql/customers";
import { GET_DELAY_NOTIFICATION_TEMPLATES } from "../gql/templates";
import {
  Activity,
  APIError,
  Attachment,
  Changelog,
  CloseInteractionPayload,
  CompanyWithCustomerCompanyAssociationId,
  Customer,
  Entitlement,
  InteractionNotificationPreview,
  Note,
  ProductWithSubscriptionId,
  RowSelection,
  Template,
  Ticket,
  User,
} from "../helpers/shared-interfaces";
import {
  handleValidationErrors,
  loadSavedFilters,
  request,
  responseHandler,
  getStateColor,
  formatSnakeCase,
  generateDisplayErrorMessage,
  showTotalPagination,
} from "../helpers/utilities";
import {
  ASSIGN_ATTACHMENT_TO_COMPANY_INTERACTION,
  ASSIGN_NOTE_TO_COMPANY_INTERACTION,
  CLOSE_COMPANY_INTERACTION,
  COMPANY_INTERACTIONS_QUERY,
  CREATE_COMPANY_TICKET,
  GET_COMPANY_INTERACTION_CHANGELOG,
  GET_COMPANY_INTERACTION_QUERY,
  HOLD_COMPANY_INTERACTION,
  OPEN_COMPANY_INTERACTION,
  UPDATE_COMPANY_INTERACTION,
} from "../gql/companies";
import { CREATE_NOTE } from "../gql/general";
import { DEBOUNCE_TIME } from "../helpers/constants";



interface State {
  isFiltering: boolean;
  customerInteractions: Activity[];
  companyInteractions: Activity[];
  filteredcustomerInteractions: Activity[];
  filteredcompanyInteractions: Activity[];
  filterBy: string;
  isLoading: boolean;
  isLoadingCompany: boolean;
  showDialog: boolean;
  viewInteraction: boolean;
  viewCustomerInteraction: boolean;
  viewCompanyInteraction: boolean;
  currentCustomerInteraction: Activity | undefined;
  isEditingInteraction: boolean;
  isTicketLoading: boolean;
  canCreateTicket: boolean;
  customerTickets: Ticket[];
  companyTickets: Ticket[];
  currentCustomerInteractionId?: string;
  customer?: Customer;
  id: string | number;
  interactionsChangeLogs: Changelog[];
  attachmentIds: string[];
  attachments: Attachment[];
  customerNotes: Note[];
  companyNotes: Note[];
  interactionView: string;
  isCustomersInteraction: boolean;
  isCompanyInteraction: boolean;
  currentCompanyInteraction: Activity | undefined;
  currentCompanyInteractionId?: string;
  selectedFieldsToDisplay: IField[];
  showCustomerAdvancedSearchModal: boolean;
  showCompanyAdvancedSearchModal: boolean;
  removedField?: IField;
  customerInteractionPage: number;
  companyInteractionPage: number;
  customerInteractionCount: number;
  companyInteractionCount: number;
  q: string;
  selectedIdsToExport: React.Key[];
  pageSize: number;
  showDelayUpdateModal: boolean;
  isDelayTemplatesLoading: boolean;
  isDelayPreviewLoading: boolean;
  isSendingDelayUpdate: boolean;
  delayNotificationTemplates: Template[];
  selectedDelayTemplateId?: string;
  delayUpdateComment: string;
  delayNotificationPreview?: InteractionNotificationPreview | null;
}

interface Props extends RouteComponentProps<RouteParams> {
  entitlement: Entitlement;
  user: User;
  activityId?: string;
}

interface RouteParams {
  id: string;
}

enum ViewTypes {
  customerInteractions = "Customer Interactions",
  companyInteractions = "Company Interactions",
}

interface Action {
  title: string;
  value: number;
}

export const customerPetraIdColumn: ColumnType<Activity>[] = [
  {
    title: "Petra ID",
    dataIndex: ["customer", "petraId"],
    key: "Petra Id",
    render(text: string, record: Activity): JSX.Element {
      return (
        <NavLink to={`/customers/${record.customer?.id}`}>
          <span>{text}</span>
        </NavLink>
      );
    },
  },
];

export const companyPetraIdColumn: ColumnType<Activity>[] = [
  {
    title: "Petra ID",
    dataIndex: ["company", "petraId"],
    key: "Petra Id",
    render(text: string, record: Activity): JSX.Element {
      return (
        <NavLink to={`/companies/${record.company?.id}`}>
          <span>{text}</span>
        </NavLink>
      );
    },
  },
];

export function ColumnCreator(
  toggleViewInteractionSection: (currentActivityId: string) => void,
): ColumnType<Activity>[] {
  return [
    {
      title: "Activity ID",
      dataIndex: "id",
      key: "id",
      render(id: string): JSX.Element {
        return <span>{id}</span>;
      },
    },
    {
      title: "Activity Date",
      dataIndex: "activityDate",
      key: "Activity Date",
      render(text: string, record: Activity): JSX.Element {
        return (
          <span>
            {record.activityDate
              ? moment(record.activityDate, "YYYY-MM-DDTHH:mm:ss.SSSZ").format(
                  "LL",
                )
              : "N/A"}
          </span>
        );
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "Description",
      width: 700,
    },
    {
      title: "Closed By",
      dataIndex: "closedBy",
      key: "closedBy",
      render(text: string) {
        return <p>{text ? text : "N/A"}</p>;
      },
    },
    {
      title: "Closure Type",
      dataIndex: "closureType",
      key: "closureType",
      render(text: string) {
        return <p style={{ textTransform: "capitalize" }}>{text ? text : "N/A"}</p>;
      },
    },
    {
      title: "Sub Category",
      dataIndex: ["subCategory", "name"],
      key: "SubCategory",
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      render(text: string, record: Activity): JSX.Element {
        return (
          <Tag
            color={getStateColor(record.status)}
            style={{ textTransform: "capitalize" }}
          >
            {formatSnakeCase(record.status)}
          </Tag>
        );
      },
    },
    {
      title: "Interaction Source",
      dataIndex: ["interactionSource", "name"],
      key: "Interaction Source",
      render(text: string, record: Activity): JSX.Element {
        return <p>{record.interactionSource.name}</p>;
      },
    },
  ];
}

export function InteractionsTableFactory(
  data: Activity[],
  toggleViewInteractionSection: (currentActivity: string) => void,
  interactionView: string,
  nextPage: (page: number, pageSize: number) => {},
  count: number,
  currentPage: number,
  pageSize: number,
  isLoading: boolean,
  rowSelection?: RowSelection,
): JSX.Element {
  const finalColumns =
    interactionView === ViewTypes.customerInteractions
      ? [
          ...customerPetraIdColumn,
          ...ColumnCreator(toggleViewInteractionSection),
        ]
      : [
          ...companyPetraIdColumn,
          ...ColumnCreator(toggleViewInteractionSection),
        ];

  [finalColumns[0], finalColumns[1]] = [finalColumns[1], finalColumns[0]];

  const tempData: Activity[] = data.map((activity: Activity): Activity => {
    return { ...activity, key: activity.id };
  });

  return (
    <Table
      loading={isLoading}
      scroll={{ x: 1100 }}
      columns={finalColumns}
      dataSource={tempData}
      onRow={(record) => ({
        onClick: () => {
          toggleViewInteractionSection(record.id);
        },
        style: { cursor: "pointer" },
      })}
      pagination={{
        defaultPageSize: pageSize,
        total: count,
        current: currentPage,
        onChange: nextPage,
        showTotal: showTotalPagination,
      }}
      rowSelection={rowSelection}
    />
  );
}
export default class Interactions extends React.Component<Props, State> {
  reference: RefObject<FormInstance>;
  noteReference: RefObject<FormInstance>;
  ticketReference: RefObject<FormInstance>;

  constructor(props: Props) {
    super(props);
    this.state = {
      showDialog: false,
      filterBy: "",
      isLoading: true,
      isLoadingCompany: true,
      isFiltering: false,
      customerInteractions: [],
      companyInteractions: [],
      viewInteraction: false,
      currentCustomerInteraction: undefined,
      isEditingInteraction: false,
      isTicketLoading: false,
      canCreateTicket: false,
      customerTickets: [],
      companyTickets: [],
      currentCustomerInteractionId: "",
      customer: {} as Customer,
      id: "",
      interactionsChangeLogs: [],
      attachmentIds: [],
      attachments: [],
      customerNotes: [],
      companyNotes: [],
      interactionView: ViewTypes.customerInteractions,
      viewCompanyInteraction: false,
      viewCustomerInteraction: false,
      isCustomersInteraction: true,
      isCompanyInteraction: false,
      currentCompanyInteraction: undefined,
      currentCompanyInteractionId: "",
      filteredcompanyInteractions: [],
      filteredcustomerInteractions: [],
      selectedFieldsToDisplay: [],
      showCompanyAdvancedSearchModal: false,
      showCustomerAdvancedSearchModal: false,
      customerInteractionPage: 1,
      companyInteractionPage: 1,
      customerInteractionCount: 0,
      companyInteractionCount: 0,
      q: "",
      selectedIdsToExport: [],
      pageSize: 25,
      showDelayUpdateModal: false,
      isDelayTemplatesLoading: false,
      isDelayPreviewLoading: false,
      isSendingDelayUpdate: false,
      delayNotificationTemplates: [],
      selectedDelayTemplateId: undefined,
      delayUpdateComment: "",
      delayNotificationPreview: null,
    };

    this.onSelectChange = this.onSelectChange.bind(this);
    this.customerInteractionsNextPage =
      this.customerInteractionsNextPage.bind(this);
    this.companyInteractionsNextPage =
      this.companyInteractionsNextPage.bind(this);

    this.reference = React.createRef<FormInstance>();
    this.noteReference = React.createRef<FormInstance>();
    this.ticketReference = React.createRef<FormInstance>();
  }

  onSelectChange(newSelectedIdsToExport: React.Key[]) {
    this.setState({
      selectedIdsToExport: Array.from(newSelectedIdsToExport).map(
        (item) => item,
      ),
    });
  }

  triggerDelayedSearch = debounce(async (): Promise<void> => {
    if (this.state.isCustomersInteraction) {
      return this.getAllCustomerInteractions();
    } else {
      return this.getAllCompanyInteractions();
    }
  }, DEBOUNCE_TIME);

  handleSearchInput = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    e.persist();
    this.setState({
      q: e.target.value,
      isFiltering: true,
      customerInteractionPage: 1,
      companyInteractionPage: 1,
    });
    const filterBy: string = e.target.value;

    if (filterBy.length === 0) {
      this.setState({ isFiltering: false });
      return;
    }

    this.triggerDelayedSearch();
  };

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  setNoteRef = (ref: React.RefObject<FormInstance>): void => {
    this.noteReference = ref;
  };

  setTicketRef = (ref: React.RefObject<FormInstance>): void => {
    this.ticketReference = ref;
  };

  getAllCustomerInteractions = async (): Promise<void> => {
    const { customerInteractionPage, q, pageSize } = this.state;
    this.setState({ isLoading: true });
    try {
      const { data: { data } }: any = await request({
        query: CUSTOMER_INTERACTIONS_QUERY,
        variables: { page: customerInteractionPage, q, numberPerPage: pageSize },
      });
      this.setState({
        customerInteractions: data?.customerInteractionSearch?.results,
        customerInteractionCount: data?.customerInteractionSearch?.count,
      });
    } catch (error) {
      message.error(`Error fetching customer interactions - ${generateDisplayErrorMessage(error)}`);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  getAllCompanyInteractions = async (): Promise<void> => {
    const { companyInteractionPage, q, pageSize } = this.state;
    this.setState({ isLoadingCompany: true });
    try {
      const { data: { data } }: any = await request({
        query: COMPANY_INTERACTIONS_QUERY,
        variables: { page: companyInteractionPage, q, numberPerPage: pageSize },
      });
      this.setState({
        companyInteractions: data?.companyInteractionSearch?.results,
        companyInteractionCount: data?.companyInteractionSearch?.count,
      });
    } catch (error) {
      message.error(`Error fetching company interactions - ${generateDisplayErrorMessage(error)}`);
    } finally {
      this.setState({ isLoadingCompany: false });
    }
  };

  customerInteractionsNextPage = async (
    page: number,
    pageSize: number,
  ): Promise<void> => {
    this.setState(
      {
        customerInteractionPage: page,
        pageSize: pageSize,
      },
      async () => {
        if (this.state.selectedFieldsToDisplay.length < 1) {
          await this.getAllCustomerInteractions();
        }
      },
    );
  };

  companyInteractionsNextPage = async (
    page: number,
    pageSize: number,
  ): Promise<void> => {
    this.setState(
      {
        companyInteractionPage: page,
        pageSize: pageSize,
      },
      async () => {
        if (this.state.selectedFieldsToDisplay.length < 1) {
          await this.getAllCompanyInteractions();
        }
      },
    );
  };

  redirectToNewPortPage = (): void => {
    this.props.history.push("/interactions/new");
  };

  close = async (): Promise<void> => {
    this.setState({
      viewCompanyInteraction: false,
      viewCustomerInteraction: false,
    });
  };

  async componentDidMount(): Promise<void> {
    if (loadSavedFilters("customerInteraction").length < 1) {
      await Promise.all([
        this.getAllCustomerInteractions(),
        this.getAllCompanyInteractions()
      ]);
    }
  }

  showInteraction = async (id: string): Promise<void> => {
    const customerInteraction =
      this.state.interactionView === ViewTypes.customerInteractions;

    this.setState(
      {
        viewInteraction: false,
        currentCustomerInteractionId: customerInteraction ? id : "",
        currentCompanyInteractionId: customerInteraction ? "" : id,
      },
      () => {
        customerInteraction
          ? this.getCustomerInteraction()
          : this.getCompanyInteraction();
      },
    );
  };

  toggleInteractionEdit = (): void => {
    this.setState({ isEditingInteraction: !this.state.isEditingInteraction });
  };

  mutateCustomerInteractions = (interaction: Activity): void => {
    if (!this.state.currentCustomerInteraction) return;
    this.setState({
      currentCustomerInteraction: interaction,
    });
  };

  mutateCompanynteractions = (interaction: Activity): void => {
    if (!this.state.currentCompanyInteraction) return;
    this.setState({
      currentCompanyInteraction: interaction,
    });
  };

  updateCustomerInteraction = async (): Promise<void> => {
    const fieldsToBeValidated: string[] = [
      "description",
      "comment",
      "complaintType",
      "contactInformation",
      "revertDate",
    ];
    interface Response {
      customerInteraction: Activity;
    }

    if (!this.reference.current) return;

    let data;
    try {
      data = await this.reference.current.validateFields(fieldsToBeValidated);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }
    const interaction = this.state.currentCustomerInteraction;

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: UPDATE_CUSTOMER_INTERACTION,
        variables: {
          ...interaction,
          interactionSourceId: interaction?.interactionSource.id,
          subCategoryId: interaction?.subCategory.id,
          userId: interaction?.user?.id,
          customerId: interaction?.customer?.id,
          comment: "",
          ...data,
        },
      });

      const { mutation } = responseHandler<Response>(
        response,
        "updateCustomerInteraction",
      );

      this.getCustomerInteraction();
      this.reference.current?.resetFields(fieldsToBeValidated);
      message.success("Interaction successfully updated!");
      this.toggleInteractionEdit();
      this.getCustomerInteractionChangeLog(mutation.customerInteraction.id);
      this.getAllCustomerInteractions();
    } catch (error) {
      Sentry.captureException(error);
    }
    this.setState({ isLoading: false });
  };

  getCustomerInteractionChangeLog = async (customerInteractionId: string): Promise<void> => {
    interface Response {
      data: {
        customerInteractionChangeLogs: Changelog[];
      };
    }
    try {
      const { data }: AxiosResponse<Response> = await request({
        query: GET_CUSTOMER_INTERACTION_CHANGELOG,
        variables: {
          customerInteractionId,
        },
      });
      this.setState({
        interactionsChangeLogs: data.data.customerInteractionChangeLogs,
      });
    } catch (error) {
      // Fail silently
    }
  };

  getCompanyInteractionChangeLog = async (companyInteractionId: string): Promise<void> => {
    interface Response {
      data: {
        companyInteractionChangeLogs: Changelog[];
      };
    }
    try {
      const { data }: AxiosResponse<Response> = await request({
        query: GET_COMPANY_INTERACTION_CHANGELOG,
        variables: {
          companyInteractionId,
        },
      });
      this.setState({
        interactionsChangeLogs: data.data.companyInteractionChangeLogs,
      });
    } catch (error) {
      // Fail silently
    }
  };

  assignNoteToInteraction = async (props: {
    activityId: string;
    noteId: string;
  }): Promise<void> => {
    interface Response {
      customerInteractionNote: {
        note: Note;
      };
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: ASSIGN_NOTE_TO_CUSTOMER_INTERACTION,
        variables: {
          noteId: props.noteId,
          customerInteractionId: props.activityId,
        },
      });

      const { err } = responseHandler<Response>(
        response,
        "assignNoteToCustomerInteraction",
      );

      if (err) {
        message.error(`failed to assign note to interaction - ${generateDisplayErrorMessage(err)}`);
      } else {
        this.getCustomerInteraction();
        message.success("Note successfully added!");
      }
    } catch (error) {
      Sentry.captureException(error);
    }

    this.setState({ isLoading: false });
  };

  getCustomerInteraction = async (): Promise<void> => {
    const id = this.state.currentCustomerInteractionId;

    this.setState({ isLoading: true });
    try {
      const { data }: AxiosResponse<{
        data: {
          customerInteraction: Activity;
        };
      }> = await request({
        query: GET_CUSTOMER_INTERACTION_QUERY,
        variables: { interactionId: id },
      });
      this.setState({
        currentCustomerInteraction: data.data.customerInteraction,
        customerNotes: data.data.customerInteraction.notes,
        viewCustomerInteraction: true
      });
    } catch (error) {
      message.error(`Error fetching interaction - ${generateDisplayErrorMessage(error)}`);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  getCompanyInteraction = async (): Promise<void> => {
    const id = this.state.currentCompanyInteractionId;

    this.setState({ isLoadingCompany: true });
    try {
      const { data }: AxiosResponse<{
        data: {
          companyInteraction: Activity;
        };
      }> = await request({
        query: GET_COMPANY_INTERACTION_QUERY,
        variables: { interactionId: id },
      });
      this.setState({
        currentCompanyInteraction: data.data.companyInteraction,
        companyNotes: data.data.companyInteraction.notes,
        viewCompanyInteraction: true,
      });
    } catch (error) {
      message.error(`Error fetching interaction - ${generateDisplayErrorMessage(error)}`);
    } finally {
      this.setState({
        isLoadingCompany: false,
      });
    }
  };

  createNote = async (): Promise<void> => {
    const { user } = this.props;
    const customerInteraction =
      this.state.interactionView === ViewTypes.customerInteractions;

    interface Response {
      note: Note;
    }

    const fieldsToBeValidated: string[] = ["description"];

    if (!this.noteReference.current) return;

    let data;

    try {
      data = await this.noteReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true, isLoadingCompany: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_NOTE,
        variables: {
          description: data.description,
          userId: user.id,
        },
      });

      const { mutation } = responseHandler<Response>(response, "createNote");
      customerInteraction
        ? await this.assignNoteToInteraction({
            noteId: mutation.note.id,
            activityId: this.state?.currentCustomerInteractionId as string,
          })
        : await this.assignNoteToCompanyInteraction({
            noteId: mutation.note.id,
            companyInteractionId: this.state
              ?.currentCompanyInteractionId as string,
          });
      this.noteReference.current.resetFields(fieldsToBeValidated);
    } catch (error) {
      Sentry.captureException(error);
    }
    this.setState({ isLoading: false, isLoadingCompany: false });
  };

  toggleTicketCreate = (): void => {
    this.setState({ canCreateTicket: !this.state.canCreateTicket });
  };

  createCustomerTicket = async (): Promise<void> => {
    const { currentCustomerInteractionId } = this.state;
    const fieldsToBeValidated = [
      "title",
      "description",
      "email",
      "firstName",
      "lastName",
      "source",
      "summary",
      "issue_type",
      "project",
      "mentions",
      "ticketSubCategoryId",
      "type",
    ];
    interface Response {
      ticket: Ticket;
    }

    if (!this.ticketReference.current) return;

    let data;

    try {
      data = await this.ticketReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isTicketLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_CUSTOMER_TICKET,
        variables: {
          customerInteractionId: currentCustomerInteractionId,
          properties: {
            ...data,
            first_name: data.firstName,
            last_name: data.lastName,
            group: "Users",
          },
          source: data.source,
          ticketSubCategoryId: data.ticketSubCategoryId,
        },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "createCustomerInteractionTicket",
      );
      if (err) {
        message.error(`Could Not Create Ticket - ${generateDisplayErrorMessage(err)}`);
      } else {
        this.getCustomerInteraction();
        this.setState((oldState) => ({
          customerTickets: [...oldState.customerTickets, mutation.ticket],
          canCreateTicket: !this.state.canCreateTicket,
        }));
        this.ticketReference.current.resetFields(fieldsToBeValidated);
      }
    } catch (error) {
      Sentry.captureException(error);
    }
    this.setState({ isTicketLoading: false });
  };

  changeCustomerInteractionStatus = async (
    query: string,
    mutationName: string,
    closePayload?: CloseInteractionPayload,
  ): Promise<boolean> => {
    const customerInteractionId = this.state.currentCustomerInteractionId;
    try {
      this.setState({ isLoading: true });
      const response: AxiosResponse = await request({
        query,
        variables: {
          customerInteractionId,
          closureType: closePayload?.closureType,
          templateId: closePayload?.templateId,
          transferRequestId: closePayload?.transferRequestId,
          sendSurvey: closePayload?.sendSurvey,
          customComment: closePayload?.customComment,
          recipientOverride: closePayload?.recipientOverride,
        },
      });

      const { err } = responseHandler<Response>(response, mutationName);
      if (err) {
        message.error(`Failed to change status of interaction - ${generateDisplayErrorMessage(err)}`);
        return false;
      }
      message.success("Interaction status updated successfully");
      this.getCustomerInteraction();
      this.getAllCustomerInteractions();
      return true;
    } catch (error) {
      Sentry.captureException(error);
      return false;
    } finally {
      this.setState({ isLoading: false });
    }
  };

  changeCompanyInteractionStatus = async (
    query: string,
    mutationName: string,
    closePayload?: CloseInteractionPayload,
  ): Promise<boolean> => {
    const companyInteractionId = this.state.currentCompanyInteractionId;
    try {
      const response: AxiosResponse = await request({
        query,
        variables: {
          companyInteractionId,
          closureType: closePayload?.closureType,
          templateId: closePayload?.templateId,
          sendSurvey: closePayload?.sendSurvey,
          customComment: closePayload?.customComment,
        },
      });

      const { err } = responseHandler<Response>(response, mutationName);
      if (err) {
        message.error(`Failed to change status of interaction - ${generateDisplayErrorMessage(err)}`);
        return false;
      }
      message.success("Interaction status updated successfully");
      this.getCompanyInteraction();
      this.getAllCompanyInteractions();
      return true;
    } catch (error) {
      Sentry.captureException(error);
      return false;
    }
  };

  holdCustomerInteraction = async (): Promise<void> => {
    await this.changeCustomerInteractionStatus(
      HOLD_CUSTOMER_INTERACTION,
      "holdCustomerInteraction",
    );
  };

  openCustomerInteraction = async (): Promise<void> => {
    await this.changeCustomerInteractionStatus(
      OPEN_CUSTOMER_INTERACTION,
      "openCustomerInteraction",
    );
  };

  closeCustomerInteraction = async (
    closePayload: CloseInteractionPayload,
  ): Promise<boolean> => {
    return await this.changeCustomerInteractionStatus(
      CLOSE_CUSTOMER_INTERACTION,
      "closeCustomerInteraction",
      closePayload,
    );
  };

  openDelayUpdateModal = (): void => {
    this.triggerDelayPreviewRefresh.cancel();
    this.setState(
      (prevState) => ({
        showDelayUpdateModal: !prevState.showDelayUpdateModal,
        selectedDelayTemplateId: undefined,
        delayUpdateComment: "",
        delayNotificationPreview: null,
      }),
      () => {
        if (
          this.state.showDelayUpdateModal &&
          this.state.delayNotificationTemplates.length === 0
        ) {
          this.getDelayNotificationTemplates();
        }
      },
    );
  };

  getDelayNotificationTemplates = async (): Promise<void> => {
    this.setState({ isDelayTemplatesLoading: true });
    try {
      const {
        data: { data },
      }: AxiosResponse<{
        data: { templates: Template[] };
      }> = await request({ query: GET_DELAY_NOTIFICATION_TEMPLATES });

      this.setState({
        delayNotificationTemplates: data.templates || [],
      });
    } catch (error) {
      message.error(`Failed to load delay notification templates - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isDelayTemplatesLoading: false });
    }
  };

  loadDelayNotificationPreview = async (
    customerInteractionId: string,
    templateId: string,
    comment: string,
  ): Promise<void> => {
    this.setState({ isDelayPreviewLoading: true });

    try {
      const variables: {
        customerInteractionId: string;
        templateId: string;
        comment?: string;
      } = {
        customerInteractionId,
        templateId,
      };

      if (comment.trim() !== "") {
        variables.comment = comment;
      }

      const response: AxiosResponse<{
        data: {
          customerInteractionDelayUpdatePreview: {
            preview: InteractionNotificationPreview | null;
            errors: APIError[];
          };
        };
      }> = await request({
        query: GET_CUSTOMER_INTERACTION_NOTIFICATION_PREVIEW,
        variables,
      });

      const previewResponse =
        response.data.data.customerInteractionDelayUpdatePreview;

      if (previewResponse.errors?.length) {
        message.error(generateDisplayErrorMessage(previewResponse.errors));
        this.setState({ delayNotificationPreview: null });
        return;
      }

      this.setState({
        delayNotificationPreview: previewResponse.preview,
      });
    } catch (error) {
      message.error(`Failed to load delay notification preview - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isDelayPreviewLoading: false });
    }
  };

  triggerDelayPreviewRefresh = debounce(
    async (
      customerInteractionId: string,
      templateId: string,
      comment: string,
    ): Promise<void> => {
      this.loadDelayNotificationPreview(
        customerInteractionId,
        templateId,
        comment,
      );
    },
    DEBOUNCE_TIME,
  );

  handleDelayTemplateChange = async (templateId: string): Promise<void> => {
    const customerInteractionId = this.state.currentCustomerInteractionId;
    if (!customerInteractionId) return;

    this.triggerDelayPreviewRefresh.cancel();
    this.setState({
      selectedDelayTemplateId: templateId,
      delayUpdateComment: "",
      delayNotificationPreview: null,
    });

    this.loadDelayNotificationPreview(customerInteractionId, templateId, "");
  };

  handleDelayCommentChange = (delayUpdateComment: string): void => {
    const { currentCustomerInteractionId, selectedDelayTemplateId } =
      this.state;

    this.setState({
      delayUpdateComment,
      delayNotificationPreview: null,
    });

    if (!currentCustomerInteractionId || !selectedDelayTemplateId) return;

    this.triggerDelayPreviewRefresh(
      currentCustomerInteractionId,
      selectedDelayTemplateId,
      delayUpdateComment,
    );
  };

  selectedDelayTemplate = (): Template | undefined => {
    return this.state.delayNotificationTemplates.find(
      (template) => template.id === this.state.selectedDelayTemplateId,
    );
  };

  selectedDelayTemplateRequiresCustomComment = (): boolean => {
    return (
      this.selectedDelayTemplate()?.body.includes("{custom_comment}") || false
    );
  };

  sendDelayUpdate = async (): Promise<void> => {
    const {
      currentCustomerInteractionId,
      selectedDelayTemplateId,
      delayUpdateComment,
    } = this.state;
    if (!currentCustomerInteractionId || !selectedDelayTemplateId) return;
    if (
      this.selectedDelayTemplateRequiresCustomComment() &&
      delayUpdateComment.trim() === ""
    ) {
      message.error("Comment is required for the selected template");
      return;
    }

    interface Response {
      customerInteraction: Activity;
      errors: APIError[];
    }

    this.triggerDelayPreviewRefresh.cancel();
    this.setState({ isSendingDelayUpdate: true });

    try {
      const variables: {
        customerInteractionId: string;
        templateId: string;
        comment?: string;
      } = {
        customerInteractionId: currentCustomerInteractionId,
        templateId: selectedDelayTemplateId,
      };

      if (delayUpdateComment.trim() !== "") {
        variables.comment = delayUpdateComment;
      }

      const response: AxiosResponse = await request({
        query: SEND_CUSTOMER_INTERACTION_DELAY_UPDATE,
        variables,
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "sendCustomerInteractionDelayUpdate",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
        return;
      }

      message.success("Delay update sent successfully");
      if (mutation?.customerInteraction) {
        const existing = this.state.customerInteractions.find(
          (i) => i.id === mutation.customerInteraction.id,
        );
        if (existing) {
          this.mutateCustomerInteractions({
            ...existing,
            revertDate: mutation.customerInteraction.revertDate,
          });
        }
      }
      this.setState({
        showDelayUpdateModal: false,
        selectedDelayTemplateId: undefined,
        delayUpdateComment: "",
        delayNotificationPreview: null,
      });
      this.getAllCustomerInteractions();
      this.getCustomerInteractionChangeLog(currentCustomerInteractionId);
    } catch (error) {
      message.error(`Failed to send delay update - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isSendingDelayUpdate: false });
    }
  };

  holdCompanyInteraction = async (): Promise<void> => {
    await this.changeCompanyInteractionStatus(
      HOLD_COMPANY_INTERACTION,
      "holdCompanyInteraction",
    );
  };

  openCompanyInteraction = async (): Promise<void> => {
    await this.changeCompanyInteractionStatus(
      OPEN_COMPANY_INTERACTION,
      "openCompanyInteraction",
    );
  };

  closeCompanyInteraction = async (
    closePayload?: CloseInteractionPayload,
  ): Promise<boolean> => {
    return await this.changeCompanyInteractionStatus(
      CLOSE_COMPANY_INTERACTION,
      "closeCompanyInteraction",
      closePayload,
    );
  };

  removeAttachment = (attachmentId: string): void => {
    this.setState((state) => ({
      attachments: state.attachments.filter(
        (attachment) => attachment.id !== attachmentId,
      ),
      attachmentIds: state.attachmentIds.filter((id) => id !== attachmentId),
    }));
  };

  updateAttachments = (attachment: Attachment): void => {
    this.setState((state) => ({
      attachments: [...state.attachments, attachment],
      attachmentIds: [...state.attachmentIds, attachment.id],
    }));
  };

  createCustomerInteraction = async (
    ccIds?: CompanyWithCustomerCompanyAssociationId[],
    customerSubscriptionIds?: ProductWithSubscriptionId[],
  ): Promise<void> => {
    console.log("test");
  };

  fakeToggleInteraction = () => {
    console.log("test");
  };

  fakeFunction = (currentActivityId: string): void => {
    console.log(currentActivityId);
  };

  onInteractionChange = (interactionView: string): void => {
    const customer = interactionView === ViewTypes.customerInteractions;
    this.setState({
      interactionView: interactionView,
      isCompanyInteraction: customer ? false : true,
      isCustomersInteraction: customer ? true : false,
    });
  };

  getView = (): JSX.Element => {
    let view;
    // rowSelection object indicates the need for row selection
    const rowSelection: RowSelection = {
      selectedIdsToExport: this.state.selectedIdsToExport,
      type: "checkbox",
      onChange: this.onSelectChange,
    };

    const isLoading = this.state.isCustomersInteraction ? this.state.isLoading : this.state.isLoadingCompany;

    if (this.state.isCustomersInteraction) {
      view = InteractionsTableFactory(
        this.state.customerInteractions,
        this.showInteraction,
        this.state.interactionView,
        this.customerInteractionsNextPage,
        this.state.customerInteractionCount,
        this.state.customerInteractionPage,
        this.state.pageSize,
        isLoading,
        rowSelection,
      );
    } else {
      view = InteractionsTableFactory(
        this.state.companyInteractions,
        this.showInteraction,
        this.state.interactionView,
        this.companyInteractionsNextPage,
        this.state.companyInteractionCount,
        this.state.companyInteractionPage,
        this.state.pageSize,
        isLoading,
        rowSelection,
      );
    }
    return view;
  };

  updateCompanyInteraction = async (): Promise<void> => {
    const fieldsToBeValidated: string[] = [
      "description",
      "dueDate",
      "comment",
      "complaintType",
    ];

    interface Response {
      companyInteraction: Activity;
    }

    if (!this.reference.current) return;

    let data;
    try {
      data = await this.reference.current.validateFields(fieldsToBeValidated);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    const interaction = this.state.currentCompanyInteraction;

    this.setState({ isLoadingCompany: true });

    try {
      const response: AxiosResponse = await request({
        query: UPDATE_COMPANY_INTERACTION,
        variables: {
          ...interaction,
          interactionSourceId: interaction?.interactionSource.id,
          subCategoryId: interaction?.subCategory.id,
          userId: interaction?.user?.id,
          companyId: interaction?.company?.id,
          ...data,
        },
      });

      const { mutation } = responseHandler<Response>(
        response,
        "updateCompanyInteraction",
      );

      this.getCompanyInteraction();
      this.reference.current?.resetFields(fieldsToBeValidated);
      message.success("Interaction successfully updated!");
      this.toggleInteractionEdit();
      this.getCompanyInteractionChangeLog(mutation.companyInteraction.id);
      this.getAllCompanyInteractions();
    } catch (error) {
      Sentry.captureException(error);
    }
    this.setState({ isLoadingCompany: false });
  };

  createTicket = async (): Promise<void> => {
    const { currentCompanyInteractionId } = this.state;
    const fieldsToBeValidated = [
      "title",
      "description",
      "email",
      "firstName",
      "lastName",
      "source",
      "summary",
      "issue_type",
      "project",
      "mentions",
    ];
    interface Response {
      ticket: Ticket;
    }

    if (!this.ticketReference.current) return;

    let data;

    try {
      data = await this.ticketReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_COMPANY_TICKET,
        variables: {
          companyInteractionId: currentCompanyInteractionId,
          properties: { ...data, group: "Users" },
          source: data.source,
        },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "createCompanyInteractionTicket",
      );
      if (err) {
        message.error(`Could Not Create Ticket - ${generateDisplayErrorMessage(err)}`);
      } else {
        this.setState((oldState) => ({
          companyTickets: [...oldState.companyTickets, mutation.ticket],
          canCreateTicket: !this.state.canCreateTicket,
        }));
        this.ticketReference.current.resetFields(fieldsToBeValidated);
      }
    } catch (error) {
      Sentry.captureException(error);
    }
    this.setState({ isLoading: false });
  };

  assignNoteToCompanyInteraction = async (props: {
    companyInteractionId: string;
    noteId: string;
  }): Promise<void> => {
    interface Response {
      data: {
        assignNoteToCustomerInteraction: {
          customerInteractionNote: {
            note: Note;
          };
        };
      };
    }

    this.setState({ isLoadingCompany: true });

    try {
      const response: AxiosResponse = await request({
        query: ASSIGN_NOTE_TO_COMPANY_INTERACTION,
        variables: {
          noteId: props.noteId,
          companyInteractionId: props.companyInteractionId,
        },
      });

      const { err } = responseHandler<Response>(
        response,
        "assignNoteToCompanyInteraction",
      );

      if (err) {
        message.error(`failed to assign note to activity - ${generateDisplayErrorMessage(err)}`);
      } else {
        this.getCompanyInteraction();
        message.success("Note successfully added!");
      }
    } catch (error) {
      Sentry.captureException(error);
    }

    this.setState({ isLoadingCompany: false });
  };

  toggleAdvancedSearchModel = (): void => {
    if (this.state.isCustomersInteraction) {
      this.setState((prevState) => ({
        showCustomerAdvancedSearchModal:
          !prevState.showCustomerAdvancedSearchModal,
      }));
    } else {
      this.setState((prevState) => ({
        showCompanyAdvancedSearchModal:
          !prevState.showCompanyAdvancedSearchModal,
      }));
    }
  };

  handleOnAdvancedSearchData = (
    data: AdvancedSearchReturn,
    fields: IField[],
  ) => {
    if (this.state.isCustomersInteraction) {
      this.setState({
        customerInteractions: data.results as Activity[],
        selectedFieldsToDisplay: fields,
        customerInteractionCount: data.count,
      });
    } else {
      this.setState({
        companyInteractions: data.results as Activity[],
        selectedFieldsToDisplay: fields,
        companyInteractionCount: data.count,
      });
    }
  };

  handleOnAdvancedSearchError = (error: string) => {
    message.error("Failed to execute query");
    Sentry.captureException(error);
  };

  handleOnFieldRemove = (field: IField) => {
    const fields = this.state.selectedFieldsToDisplay.filter(
      (f) => f.name !== field.name,
    );
    this.setState(
      {
        removedField: field,
        selectedFieldsToDisplay: fields,
      },
      () => {
        localStorage.setItem(
          "customerInteraction",
          JSON.stringify(this.state.selectedFieldsToDisplay),
        );
      },
    );

    if (fields.length === 0) this.triggerDelayedSearch();
  };

  handleNoteUpdate = (updatedNotes: Note[]) => {
    if (this.state.interactionView === ViewTypes.customerInteractions) {
      this.setState({ customerNotes: updatedNotes });
    } else {
      this.setState({ companyNotes: updatedNotes });
    }
  };

  assignAttachmentsToCustomerInteraction = async (
    attachmentIds: string[],
    customerInteractionId: string,
  ): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      data: {
        assignAttachmentsToCustomerInteraction: {
          customerInteraction: Activity;
        };
      };
    }

    return request({
      query: ASSIGN_ATTACHMENT_TO_CUSTOMER_INTERACTION,
      variables: {
        attachmentIds,
        customerInteractionId,
      },
    })
      .then(({ data }: AxiosResponse<Response>): void => {
        this.setState({
          isLoading: false,
          attachments: [],
        });
      })
      .catch((error) => {
        this.setState({ isLoading: false });
        message.error("Failed to attach files");
        Sentry.captureException(error);
      });
  };

  assignAttachmentsToCompanyInteraction = async (
    attachmentIds: string[],
    companyInteractionId: string,
  ): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      data: {
        assignAttachmentsToCompanyInteraction: {
          companyInteraction: Activity;
        };
      };
    }

    return request({
      query: ASSIGN_ATTACHMENT_TO_COMPANY_INTERACTION,
      variables: {
        attachmentIds,
        companyInteractionId,
      },
    })
      .then(({ data }: AxiosResponse<Response>): void => {
        this.setState({
          isLoading: false,
          attachments: [],
        });
      })
      .catch((error) => {
        this.setState({ isLoading: false });
        message.error("Failed to attach files");
        Sentry.captureException(error);
      });
  };

  render(): JSX.Element | null {
    const {
      isLoading,
      currentCustomerInteraction,
      customerNotes,
      companyNotes,
      currentCompanyInteraction,
      selectedFieldsToDisplay,
      removedField,
      selectedIdsToExport,
      isCustomersInteraction,
      customerInteractions,
      companyInteractions,
      customerInteractionPage,
      companyInteractionPage,
      customerInteractionCount,
      companyInteractionCount,
      pageSize,
      q,
      interactionView,
    } = this.state;

    return (
      <>
        <Card>
          <div className="deparments-table-container">
            <PageToolbar
              searchPlaceholder="Filter by PetraId, Interaction ID, Label or Source..."
              searchValue={q}
              onSearchChange={this.handleSearchInput}
              totalCount={isCustomersInteraction ? customerInteractionCount : companyInteractionCount}
              currentDataCount={isCustomersInteraction ? customerInteractions.length : companyInteractions.length}
              page={isCustomersInteraction ? customerInteractionPage : companyInteractionPage}
              pageSize={25}
              selectedCount={selectedIdsToExport.length}
              advancedSearch={{
                onOpen: this.toggleAdvancedSearchModel,
                activeFields: selectedFieldsToDisplay,
                onFieldRemove: this.handleOnFieldRemove,
              }}
              modelName="interactions"
              filters={
                <PageToolbar.Filters
                  configs={[
                    {
                      label: "Interaction View",
                      options: [
                        { value: ViewTypes.customerInteractions, label: ViewTypes.customerInteractions },
                        { value: ViewTypes.companyInteractions, label: ViewTypes.companyInteractions },
                      ],
                      selectedValues: [interactionView],
                      onChange: (values) => this.onInteractionChange(values[0]),
                      icon: <ControlOutlined />,
                      width: 250,
                      mode: "single",
                    },
                  ]}
                />
              }
              actions={
                <ExportData
                  model={isCustomersInteraction ? "customerInteraction" : "companyInteraction"}
                  dataArray={isCustomersInteraction ? customerInteractions : companyInteractions}
                  size={"large"}
                  customIds={selectedIdsToExport}
                />
              }
            />
            {isLoading ? <></> : this.getView()}
          </div>
        </Card>
        <Drawer
          title={"View Customer Interaction"}
          width={1200}
          onClose={this.close}
          visible={this.state.viewCustomerInteraction}
        >
          <InteractionSection
            forCustomer={true}
            forCompany={false}
            showRequests={false}
            updateInteraction={this.updateCustomerInteraction}
            isEditingInteraction={this.state.isEditingInteraction}
            isCreateTicketLoading={this.state.isTicketLoading}
            toggleInteractionEdit={this.toggleInteractionEdit}
            audits={undefined}
            showInteraction={false}
            toggleCreateInteractionModal={this.fakeToggleInteraction}
            isInteractionLoading={false}
            notes={customerNotes}
            customer={this.state.customer}
            tickets={this.state.customerTickets}
            interaction={currentCustomerInteraction}
            createNote={this.createNote}
            createTicket={this.createCustomerTicket}
            activityReference={this.reference}
            noteReference={this.noteReference}
            showTicketForm={this.state.canCreateTicket}
            toggleTicketCreate={this.toggleTicketCreate}
            ticketReference={this.ticketReference}
            createInteraction={this.createCustomerInteraction}
            toggleViewRequestsSection={this.fakeFunction}
            toggleViewInteractionSection={this.showInteraction}
            toggleViewRedemptionRequestsSection={this.fakeFunction}
            updateAttachments={this.updateAttachments}
            removeAttachment={this.removeAttachment}
            holdInteraction={this.holdCustomerInteraction}
            closeInteraction={this.closeCustomerInteraction}
            openInteraction={this.openCustomerInteraction}
            openDelayUpdateModal={this.openDelayUpdateModal}
            transferRequests={[]}
            entity="CustomerInteraction"
            handleNoteUpdate={this.handleNoteUpdate}
            assignAttachmentsToCustomerInteraction={
              this.assignAttachmentsToCustomerInteraction
            }
            currentInteractionId={this.state.currentCustomerInteractionId}
          />
        </Drawer>
        <Drawer
          title={"View Company Interaction"}
          width={1200}
          onClose={this.close}
          visible={this.state.viewCompanyInteraction}
        >
          <InteractionSection
            forCustomer={false}
            forCompany={true}
            showInteraction={false}
            toggleCreateInteractionModal={this.fakeToggleInteraction}
            isInteractionLoading={false}
            updateInteraction={this.updateCompanyInteraction}
            isEditingInteraction={this.state.isEditingInteraction}
            isCreateTicketLoading={this.state.isTicketLoading}
            toggleInteractionEdit={this.toggleInteractionEdit}
            audits={undefined}
            notes={companyNotes}
            customer={this.state.customer}
            tickets={this.state.companyTickets}
            interaction={currentCompanyInteraction}
            createNote={this.createNote}
            createTicket={this.createTicket}
            activityReference={this.reference}
            noteReference={this.noteReference}
            showTicketForm={this.state.canCreateTicket}
            toggleTicketCreate={this.toggleTicketCreate}
            ticketReference={this.ticketReference}
            createInteraction={this.createCustomerInteraction}
            toggleViewRequestsSection={this.fakeFunction}
            toggleViewInteractionSection={this.showInteraction}
            toggleViewRedemptionRequestsSection={this.fakeFunction}
            updateAttachments={this.updateAttachments}
            removeAttachment={this.removeAttachment}
            holdInteraction={this.holdCompanyInteraction}
            closeInteraction={this.closeCompanyInteraction}
            openInteraction={this.openCompanyInteraction}
            transferRequests={[]}
            entity="CompanyInteraction"
            handleNoteUpdate={this.handleNoteUpdate}
            assignAttachmentsToCompanyInteraction={
              this.assignAttachmentsToCustomerInteraction
            }
            currentInteractionId={this.state.currentCompanyInteractionId}
          />
        </Drawer>
        <AdvancedSearchModal
          toggleModal={this.toggleAdvancedSearchModel}
          showModal={
            this.state.showCustomerAdvancedSearchModal ||
            this.state.showCompanyAdvancedSearchModal
          }
          model={
            isCustomersInteraction
              ? "customerInteraction"
              : "companyInteraction"
          }
          onError={this.handleOnAdvancedSearchError}
          onData={this.handleOnAdvancedSearchData}
          triggerField={removedField?.name}
          pageSize={pageSize}
        />
        <DelayUpdateModal
          showModal={this.state.showDelayUpdateModal}
          templates={this.state.delayNotificationTemplates}
          selectedTemplateId={this.state.selectedDelayTemplateId}
          comment={this.state.delayUpdateComment}
          requiresComment={this.selectedDelayTemplateRequiresCustomComment()}
          preview={this.state.delayNotificationPreview}
          isLoading={
            this.state.isDelayTemplatesLoading ||
            this.state.isDelayPreviewLoading
          }
          isSending={this.state.isSendingDelayUpdate}
          onTemplateChange={this.handleDelayTemplateChange}
          onCommentChange={this.handleDelayCommentChange}
          onSend={this.sendDelayUpdate}
          onClose={this.openDelayUpdateModal}
        />
      </>
    );
  }
}
